import { useCallback, useEffect, useRef, useState } from 'react';
import { useRefreshOnFocus } from './useRefreshOnFocus';

const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

type PageResult<TItem> = {
  items: TItem[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
};

type Options<TItem, TFilters> = {
  getId: (item: TItem) => number;
  loadPage: (args: {
    page: number;
    pageSize: number;
    search: string;
    filters: TFilters;
  }) => Promise<PageResult<TItem>>;
  initialFilters: TFilters;
  errorMessage?: string;
  pageSize?: number;
  autoLoad?: boolean;
  /**
   * When true (default), refetch page 1 whenever the screen regains focus
   * after the initial load. Keeps lists current after create/edit/delete.
   */
  refreshOnFocus?: boolean;
};

export function usePaginatedMasterData<TItem, TFilters extends object>(
  options: Options<TItem, TFilters>,
) {
  const {
    getId,
    loadPage,
    initialFilters,
    errorMessage = 'Unable to load records.',
    pageSize = DEFAULT_PAGE_SIZE,
    autoLoad = true,
    refreshOnFocus = true,
  } = options;

  const [items, setItems] = useState<TItem[]>([]);
  const [isLoading, setIsLoading] = useState(autoLoad);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearchState] = useState('');
  const [filters, setFiltersState] = useState<TFilters>(initialFilters);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const mounted = useRef(true);
  const requestId = useRef(0);
  const loadingMore = useRef(false);
  const pageRef = useRef(1);
  const searchRef = useRef('');
  const filtersRef = useRef<TFilters>(initialFilters);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadPageRef = useRef(loadPage);
  const getIdRef = useRef(getId);

  loadPageRef.current = loadPage;
  getIdRef.current = getId;

  const fetchPage = useCallback(
    async (nextPage: number, mode: 'replace' | 'append' | 'refresh') => {
      const currentRequest = ++requestId.current;
      if (mode === 'append') {
        if (loadingMore.current) return;
        loadingMore.current = true;
        setIsLoadingMore(true);
      } else if (mode === 'refresh') {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      try {
        const result = await loadPageRef.current({
          page: nextPage,
          pageSize,
          search: searchRef.current.trim(),
          filters: filtersRef.current,
        });

        if (!mounted.current || currentRequest !== requestId.current) return;

        setPage(result.page);
        pageRef.current = result.page;
        setHasMore(result.page < result.totalPages);
        setItems(current => {
          if (mode === 'append') {
            const seen = new Set(current.map(item => getIdRef.current(item)));
            const merged = [...current];
            for (const item of result.items) {
              const id = getIdRef.current(item);
              if (!seen.has(id)) {
                seen.add(id);
                merged.push(item);
              }
            }
            return merged;
          }
          return result.items;
        });
      } catch {
        if (mounted.current && currentRequest === requestId.current) {
          setError(errorMessage);
        }
      } finally {
        loadingMore.current = false;
        if (mounted.current && currentRequest === requestId.current) {
          setIsLoading(false);
          setIsRefreshing(false);
          setIsLoadingMore(false);
        }
      }
    },
    [errorMessage, pageSize],
  );

  useEffect(() => {
    mounted.current = true;
    if (autoLoad) fetchPage(1, 'replace');
    return () => {
      mounted.current = false;
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [autoLoad, fetchPage]);

  // Refetch when returning from create/edit/details so lists stay current.
  useRefreshOnFocus(
    () => {
      fetchPage(1, 'refresh').catch(() => undefined);
    },
    autoLoad && refreshOnFocus,
  );

  const setSearch = useCallback(
    (value: string) => {
      setSearchState(value);
      searchRef.current = value;
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        pageRef.current = 1;
        fetchPage(1, 'replace');
      }, SEARCH_DEBOUNCE_MS);
    },
    [fetchPage],
  );

  const applyFilters = useCallback(
    (next: TFilters) => {
      setFiltersState(next);
      filtersRef.current = next;
      pageRef.current = 1;
      fetchPage(1, 'replace');
    },
    [fetchPage],
  );

  const resetFilters = useCallback(() => {
    setFiltersState(initialFilters);
    filtersRef.current = initialFilters;
    pageRef.current = 1;
    fetchPage(1, 'replace');
  }, [fetchPage, initialFilters]);

  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore.current || isLoading || isRefreshing) return;
    fetchPage(pageRef.current + 1, 'append');
  }, [fetchPage, hasMore, isLoading, isRefreshing]);

  return {
    items,
    search,
    filters,
    page,
    isLoading,
    isRefreshing,
    isLoadingMore,
    hasMore,
    error,
    load: () => fetchPage(1, 'replace'),
    refresh: () => fetchPage(1, 'refresh'),
    loadMore,
    setSearch,
    applyFilters,
    resetFilters,
    retry: () => fetchPage(1, 'replace'),
  };
}
